import React, { useMemo, useState } from 'react';
import { useParams, Link, useHistory } from 'react-router-dom';
import {
  CCard,
  CCardBody,
  CRow,
  CCol,
  CCardHeader,
  CButton,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CCardTitle,
  CDataTable,
  CFormGroup,
  CLabel,
  CInput,
} from '@coreui/react';
import Loading from '../../components/Loading';
import { formatCurrency, formatDateTime, extractErrorMessage, isSet } from '../../helpers';
import _t from 'counterpart';
import PositionTypeBadge from '../../components/PositionTypeBadge';
import PlatformBadge from '../../components/PlatformBadge';
import PositionStatusBadge from '../../components/PositionStatusBadge';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { loadPosition, closePosition, loadOrders } from '../../services/BackendService';
import { IPosition } from './position';
import { useLivePositionUpdates, useAppSelector } from '../../helpers/customHooks';
import OrderStatusBadge from '../orders/OrderStatusBadge';
import { IOrder } from '../orders/types';
import PageLayout from '../../components/PageLayout';
import { Permission } from '../../reducers/userReducer';
import Description from '../../components/Description';
import UpdatePositionModal from './UpdatePositionModal';
import { WalletPlatform } from '../wallets/types';
import toast from 'react-hot-toast';
import { isLivePosition, tradePnL } from '../../helpers/positionHelpers';

const PositionPage = () => {
  const [closePositionModalShow, closePositionModalToggle] = useState<boolean>(false);
  const [editPositionShow, setEditPositionShow] = useState<IPosition | null>(null);

  const permissions = useAppSelector((state) => state.user.permissions);

  const [forceCloseWithPrice, setForceCloseWithPrice] = useState<boolean>(false);
  const [forceClosePrice, setForceClosePrice] = useState<string>('');

  const params = useParams();
  const history = useHistory();

  const { id } = params as any;

  useLivePositionUpdates(id);

  const { data, isLoading } = useQuery<IPosition>(['position', id], () => loadPosition(id || ''), {
    onError: (e: any) => {
      const error = extractErrorMessage(e);
      toast.error(error);
    },
    retry: false,
  });

  const { data: orders, isLoading: isLoadingOrders } = useQuery(['position-order'], () => loadOrders(id || ''), {
    onError: (e: any) => {
      const error = extractErrorMessage(e);
      toast.error(error);
    },
    retry: false,
  });

  const queryClient = useQueryClient();

  const closePositionMutation = useMutation(
    (payload: { id: string; options: { sendThroughAdapter: boolean; closePrice?: number } }) =>
      closePosition(payload.id, payload.options),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries('position');
        await queryClient.invalidateQueries('position-order');
        closePositionModalToggle(false);
        setForceCloseWithPrice(false);
        setForceClosePrice('');
      },
      onError: (e: any) => {
        const error = extractErrorMessage(e);
        toast.error(error);
      },
      retry: false,
    }
  );

  const closePositionModalClose = () => {
    closePositionModalToggle(false);
    setForceCloseWithPrice(false);
    setForceClosePrice('');
  };

  const fields = useMemo(
    () => [
      { key: 'id', label: 'ID' },
      { key: 'externalId', label: _t('positions.orders.external-id') },
      { key: 'type', label: _t('positions.side') },
      { key: 'dates', label: _t('positions.orders.dates') },
      { key: 'quantity', label: _t('positions.orders.quantity') },
      { key: 'prices', label: _t('positions.orders.prices') },
      { key: 'status', label: _t('positions.status') },
    ],
    []
  );

  const slots = useMemo(
    () => ({
      externalId: ({ externalId }: any) => <td>{externalId || '-'}</td>,
      dates: ({ executedAt, placedAt }: any) => (
        <td>
          <div>{placedAt ? formatDateTime(placedAt) : '-'}</div>
          <div>{executedAt ? formatDateTime(executedAt) : '-'}</div>
        </td>
      ),
      quantity: ({ quantity, filledQuantity }: any) => (
        <td>
          <div>{quantity || '-'}</div>
          <div>{filledQuantity || '-'}</div>
        </td>
      ),
      prices: ({ averagePrice, requestPrice }: any) => (
        <td>
          <div>{requestPrice || '-'}</div>
          <div>{averagePrice || '-'}</div>
        </td>
      ),
      status: ({ status, rejectReason }: any) => (
        <td>
          <div>
            <OrderStatusBadge status={status} />
          </div>
          {rejectReason && <div>{rejectReason}</div>}
        </td>
      ),
    }),
    []
  );

  if (isLoading || !data) {
    return <Loading />;
  }

  const {
    accountId,
    size,
    closedPLInAccountCurrency,
    profitLoss,
    closeReason,
    customerId,
    externalId,
    invest,
    openedAt,
    platform,
    source,
    status,
    symbol,
    closePositionAfterSeconds,
    swap,
    takeProfit,
    stopLoss,
    commission,
    trailingStopLossPct,
    openPrice,
    closePrice,
    totalProfitLoss,
  } = data!;

  const manualClosePriceNumber = Number(forceClosePrice);
  const isManualClosePriceValid =
    forceClosePrice !== '' && !Number.isNaN(manualClosePriceNumber) && manualClosePriceNumber >= 0;

  const closePositionCall = () => {
    if (forceCloseWithPrice) {
      closePositionMutation.mutate({
        id,
        options: { sendThroughAdapter: false, closePrice: manualClosePriceNumber },
      });
      return;
    }

    closePositionMutation.mutate({
      id,
      options: { sendThroughAdapter: true },
    });
  };

  const handleOrderRowClicked = (order: IOrder) => {
    if (!permissions?.includes(Permission.VIEW_ORDERS)) {
      return;
    }
    history.push(`/orders/${order.id}`);
  };

  const showRetryClosingButton = status === 'in-closing' || status === 'error-on-closing';

  const durationLabel =
    closePositionAfterSeconds === null ? '∞' : `${closePositionAfterSeconds} ${_t('positions.seconds')}`;

  const pL = tradePnL(data);
  const isLive = isLivePosition(status);

  const onEditClicked = () => {
    setEditPositionShow(data);
  };

  const onEditClose = async (refetch?: boolean) => {
    setEditPositionShow(null);
    if (refetch) {
      await queryClient.invalidateQueries('position');
      await queryClient.invalidateQueries('position-order');
    }
  };

  const hasEditPermissions = permissions?.includes(Permission.MANAGE_POSITIONS);
  const canClosePosition = permissions?.includes(Permission.CLOSE_POSITIONS);
  // MT5 positions are read-only here: closing or editing one means asking MT5, and both endpoints
  // go through the Wintrado gateway, which holds no such position. (The retry-closing button needs
  // no such guard — `in-closing` and `error-on-closing` only ever come from the gateway's own close
  // flow, so an MT5 position never has either status.)
  const isMt5 = platform === WalletPlatform.MT5;
  const showClosePosition = data && data.status === 'open' && !isMt5;
  const showEditPosition =
    data && !isMt5 && (data.status === 'open' || data.status === 'closed' || data.status === 'new');

  return (
    <PageLayout
      title={_t.translate('positions.position.title') + ` #${id}`}
      titleAppend={
        <>
          {hasEditPermissions && (
            <div className="d-flex justify-content-between float-right mb-2">
              <div className="d-flex flex-nowrap flex-row-reverse">
                {showEditPosition && hasEditPermissions && (
                  <CButton color="primary" onClick={onEditClicked}>
                    {_t('action.edit')}
                  </CButton>
                )}
                {showClosePosition && canClosePosition && (
                  <CButton color="danger" className="mr-2" onClick={() => closePositionModalToggle(true)}>
                    {_t('positions.position.close-position')}
                  </CButton>
                )}
                {showRetryClosingButton && canClosePosition && (
                  <CButton color="danger" className="mr-2" onClick={() => closePositionModalToggle(true)}>
                    {_t('positions.position.retry-closing')}
                  </CButton>
                )}
              </div>
            </div>
          )}
        </>
      }
    >
      <CRow>
        <CCol md={12}>
          <CCard>
            <CCardBody>
              <CRow>
                <Description title={_t('positions.account-id')}>
                  <Link to={`/wallets/${accountId}`}>{accountId}</Link>
                </Description>
                {/* The id on the platform itself — the MT5 ticket for an MT5 position. The heading
                    above shows the CRM's own id, which is not what an operator reconciles with. */}
                <Description title={_t('positions.external-id')}>{externalId ?? '-'}</Description>
                <Description title={_t('positions.close-reason')}>
                  {/* MT5 keeps its own reason vocabulary, and stores `MT5_UNKNOWN_<n>` for a value
                      this build cannot name — so fall back to the raw word rather than printing
                      counterpart's missing-translation string. */}
                  {closeReason
                    ? _t(`positions.close-reasons.${closeReason}`, { fallback: closeReason })
                    : _t('positions.not-closed')}
                </Description>
              </CRow>
              <CRow>
                <Description title={_t('positions.opened-at')}>{formatDateTime(openedAt)}</Description>
                <Description title={_t('positions.status')}>
                  <PositionStatusBadge position={data} />
                </Description>
              </CRow>
              <CRow>
                <Description title={_t('positions.side')}>
                  <PositionTypeBadge position={data} />
                </Description>
                <Description title={_t('positions.platform')}>
                  <PlatformBadge platform={platform} />
                </Description>
                <Description title={_t('positions.source')}>{source ?? '-'}</Description>
              </CRow>
              <CRow>
                <Description title={_t('positions.symbol')}>{symbol}</Description>
                <Description title={_t('positions.stop-loss')}>{stopLoss ?? _t('positions.not-set')}</Description>
              </CRow>
              <CRow>
                <Description title={_t('positions.trade-pnl')}>
                  {isSet(pL) ? formatCurrency(pL) : _t('positions.not-closed')}
                </Description>
                {/* Only a partially closed position has both, and only then does splitting them
                    say anything — on a Wintrado position it would repeat the row above. */}
                {isLive && isSet(closedPLInAccountCurrency) && (
                  <Description title={_t('positions.realized-pnl')}>
                    {formatCurrency(closedPLInAccountCurrency)}
                  </Description>
                )}
                <Description title={_t('positions.total-pnl')}>
                  {isSet(totalProfitLoss) ? formatCurrency(totalProfitLoss) : _t('positions.not-closed')}
                </Description>
                <Description title={_t('orders.trailing-stop-loss-pct')}>
                  {trailingStopLossPct ? trailingStopLossPct + '%' : _t('positions.not-set')}
                </Description>
                <Description title={_t('positions.amount')}>{size}</Description>
                <Description title={_t('positions.take-profit')}>{takeProfit ?? _t('positions.not-set')}</Description>
              </CRow>
              <CRow>
                <Description title={_t('positions.duration')}>{durationLabel}</Description>
                <Description title={_t('positions.swap')}>{formatCurrency(swap === null ? 0 : swap)}</Description>

                <Description title={_t('positions.commission')}>
                  {commission ? formatCurrency(commission) : '-'}
                </Description>
                <Description title={_t('positions.invest')}>{invest ?? _t('positions.not-set')}</Description>
                <Description title={_t('positions.position.open-price')}>
                  {openPrice ?? _t('positions.not-set')}
                </Description>
                <Description title={_t('positions.position.close-price')}>
                  {closePrice ?? _t('positions.not-set')}
                </Description>
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <CRow>
        <CCol md={12}>
          <CCard>
            <CCardHeader className="d-flex justify-content-between align-items-center">
              <CCardTitle>{_t('positions.orders.title')}</CCardTitle>
            </CCardHeader>
            <CCardBody>
              <CDataTable
                items={orders}
                fields={fields}
                scopedSlots={slots}
                loading={isLoadingOrders}
                onRowClick={handleOrderRowClicked}
                clickableRows={permissions?.includes(Permission.VIEW_ORDERS)}
                hover
                striped
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <CModal show={closePositionModalShow} onClose={closePositionModalClose}>
        <CModalHeader>
          <CModalTitle>{_t('positions.position.close-position')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {showRetryClosingButton && _t('positions.position.issue-new-order')}
          {status === 'open' && _t('positions.position.close-position-question')}

          {canClosePosition && (
            <>
              <CFormGroup className="mt-3">
                <div className="d-flex align-items-center">
                  <input
                    type="checkbox"
                    checked={forceCloseWithPrice}
                    onChange={(e) => setForceCloseWithPrice(e.target.checked)}
                    id="forceCloseWithPrice"
                  />
                  <CLabel variant="checkbox" htmlFor="forceCloseWithPrice" className="ml-1">
                    {_t('positions.position.force-close-using-provided-closing-price')}
                  </CLabel>
                </div>

                <CInput
                  type="number"
                  placeholder="10.0"
                  value={forceClosePrice}
                  disabled={!forceCloseWithPrice}
                  onChange={(e) => setForceClosePrice(String((e.target as HTMLInputElement).value))}
                />

                {forceCloseWithPrice && !isManualClosePriceValid && (
                  <CLabel className="text-danger">
                    {_t('positions.position.force-close-using-provided-closing-price-error')}
                  </CLabel>
                )}
              </CFormGroup>

              {forceCloseWithPrice && (
                <div className="text-muted" style={{ fontSize: '0.9em' }}>
                  {_t('positions.position.force-close-using-provided-closing-price-description')}
                </div>
              )}
            </>
          )}
        </CModalBody>
        {canClosePosition && (
          <CModalFooter className="modal-footer--alternative">
            <CButton
              color="danger"
              onClick={closePositionCall}
              disabled={forceCloseWithPrice && !isManualClosePriceValid}
            >
              {_t('positions.position.close')}
            </CButton>
            <CButton color="secondary" onClick={closePositionModalClose}>
              {_t('action.cancel')}
            </CButton>
          </CModalFooter>
        )}
      </CModal>
      <UpdatePositionModal position={editPositionShow} onClose={onEditClose} />
    </PageLayout>
  );
};

export default PositionPage;
